import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "@/i18n/router";
import { Search, ChevronDown } from "lucide-react";
import { DOC_NAV, DOC_VERSIONS, DOCS, buildDocIndex } from "@/data/docs";
import { useLang } from "@/i18n/LanguageContext";
import { translateString } from "@/i18n/translate";

export function DocsSidebar({ active }) {
  const [query, setQuery] = useState("");
  const [version, setVersion] = useState(() => localStorage.getItem("astro_docs_version") || "2.4");
  const navigate = useNavigate();
  const { lang, tr } = useLang();
  const index = useMemo(() => buildDocIndex(), []);

  const results = query.length >= 2
    ? index.filter((d) => d.text.includes(query.toLowerCase())).slice(0, 6)
    : [];

  const onVersion = (v) => { setVersion(v); localStorage.setItem("astro_docs_version", v); };

  return (
    <aside className="lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto">
      {/* Version selector */}
      <div className="relative mb-5">
        <select
          value={version}
          onChange={(e) => onVersion(e.target.value)}
          data-testid="docs-version-select"
          className="w-full appearance-none rounded-lg border border-navy-border bg-navy-surface px-3 py-2.5 pr-9 text-sm font-medium text-white focus:border-astro-blue focus:outline-none"
        >
          {DOC_VERSIONS.map((v) => <option key={v.id} value={v.id}>{tr(v.label)}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-silver/50" />
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-silver/40" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tr("Search documentation…")}
          data-testid="docs-search-input"
          className="w-full rounded-lg border border-navy-border bg-navy-surface py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none"
        />
        {results.length > 0 && (
          <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-lg border border-navy-border bg-navy-surface shadow-2xl" data-testid="docs-search-results">
            {results.map((r) => (
              <button
                key={r.slug}
                onClick={() => { navigate(`/documentation/${r.slug}`); setQuery(""); }}
                data-testid={`docs-search-result-${r.slug}`}
                className="block w-full px-3 py-2.5 text-left text-sm text-silver/80 hover:bg-navy hover:text-white transition-colors"
              >
                <span className="font-medium text-white">{translateString(r.title, lang)}</span>
                <span className="block text-xs text-silver/40">{translateString(r.category, lang)}</span>
              </button>
            ))}
          </div>
        )}
        {query.length >= 2 && results.length === 0 && (
          <p className="absolute mt-2 w-full rounded-lg border border-navy-border bg-navy-surface px-3 py-2.5 text-sm text-silver/50" data-testid="docs-search-empty">{tr("No results.")}</p>
        )}
      </div>

      {/* Nav */}
      <nav className="space-y-6">
        {DOC_NAV.map((group) => (
          <div key={group.category}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-silver/40">{tr(group.category)}</p>
            <ul className="space-y-0.5 border-l border-navy-border">
              {group.items.map((slug) => (
                <li key={slug}>
                  <Link
                    to={`/documentation/${slug}`}
                    data-testid={`docs-nav-${slug}`}
                    className={`-ml-px block border-l-2 py-1.5 pl-4 text-sm transition-colors ${
                      active === slug
                        ? "border-astro-blue font-medium text-astro-light"
                        : "border-transparent text-silver/65 hover:border-silver/30 hover:text-white"
                    }`}
                  >
                    {translateString(DOCS[slug]?.title, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
